import { useCallback } from 'react';

import PlayGlyph from '@rover/icons/svg/play-glyph.svg';
import { Box, Flex } from '@rover/kibble/core';
import LoadingShimmer from '@rover/kibble/patterns/LoadingShimmer';
import DSTokenMap from '@rover/kibble/styles/DSTokenMap';
import useOnIntersect from '@rover/react-lib/src/hooks/useOnIntersect';

import {
  GALLERY_CONTAINER_WIDTH,
  GALLERY_IMAGE_SIZE,
  PLAY_BADGE_OPACITY,
  PLAY_BADGE_SIZE,
} from './constants';
import type { GalleryItem, PaginationConfig } from './ImageGallery.common';
import { countGalleryVideoItems, galleryItemKey, isGalleryVideoItem } from './ImageGallery.common';
import ImageWithShimmer from './ImageWithShimmer';
import { getShimmerCount, shouldLoadMore } from './utils';

type ImageGridContainerProps = {
  images: GalleryItem[];
  onImageClick: (index: number) => void;
  // Mobile only props
  // eslint-disable-next-line react/no-unused-prop-types
  onClose?: () => void;
  pagination?: PaginationConfig;
};

const SHIMMER_SIZE = parseInt(GALLERY_IMAGE_SIZE, 10);

const PLAY_BADGE_SIZE_PX = `${PLAY_BADGE_SIZE}px`;

const PLAY_BADGE_BACKGROUND = DSTokenMap.TEXT_COLOR_PRIMARY.withOpacity(PLAY_BADGE_OPACITY);
const PLAY_BADGE_FILL = `${DSTokenMap.TEXT_COLOR_PRIMARY_INVERSE}`;

const ImageGridContainer = ({
  images,
  onImageClick,
  pagination,
}: ImageGridContainerProps): JSX.Element | null => {
  const handleLoadMore = useCallback(() => {
    if (shouldLoadMore(pagination)) {
      pagination?.onLoadMore();
    }
  }, [pagination]);

  const sentinelRef = useOnIntersect(handleLoadMore, false);

  const loadedImageCount = images.length - countGalleryVideoItems(images);
  const shimmerCount = getShimmerCount(pagination, loadedImageCount);

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, ${GALLERY_IMAGE_SIZE})`,
          gridAutoRows: GALLERY_IMAGE_SIZE,
          rowGap: '4x',
          columnGap: '3x',
          margin: '0 auto',
          justifyContent: 'center',
          width: '100%',
          maxWidth: GALLERY_CONTAINER_WIDTH,
        }}
      >
        {images.map((image, index) =>
          isGalleryVideoItem(image) ? (
            // The grid shows the video's thumbnail with a play badge, never a
            // player. Opening the tile is what starts playback, in the lightbox.
            <Box key={galleryItemKey(image)} position="relative" width="fit-content">
              <ImageWithShimmer
                src={image.small}
                caption={image.video.description}
                onClick={() => onImageClick(index)}
              />
              <Flex
                position="absolute"
                top="0"
                width="100%"
                height="100%"
                justifyContent="center"
                alignItems="center"
                data-testid="gallery-play-icon"
                // The badge sits over the tile's own click target, so it has to
                // let presses through rather than swallow them.
                sx={{ pointerEvents: 'none' }}
              >
                <Flex
                  width={PLAY_BADGE_SIZE_PX}
                  height={PLAY_BADGE_SIZE_PX}
                  borderRadius="round"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ backgroundColor: PLAY_BADGE_BACKGROUND }}
                >
                  <PlayGlyph
                    height={PLAY_BADGE_SIZE_PX}
                    width={PLAY_BADGE_SIZE_PX}
                    fill={PLAY_BADGE_FILL}
                  />
                </Flex>
              </Flex>
            </Box>
          ) : (
            <ImageWithShimmer
              src={image.small}
              caption={image.caption}
              key={galleryItemKey(image)}
              onClick={() => onImageClick(index)}
            />
          )
        )}
        {pagination?.isLoadingMore &&
          Array.from({ length: shimmerCount }, (_, i) => (
            <LoadingShimmer
              key={`shimmer-${i}`}
              width={SHIMMER_SIZE}
              height={SHIMMER_SIZE}
              borderRadius="primary"
              m="0x"
              data-testid="loading-more-shimmer"
            />
          ))}
      </Box>
      {pagination?.hasMore && (
        <Box ref={sentinelRef} height="1px" aria-hidden="true" data-testid="scroll-sentinel" />
      )}
    </>
  );
};

export default ImageGridContainer;
